import { useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  MagnifyingGlass,
  Stack,
  Table as TableIcon,
  FilePdf,
  Robot,
  SignOut,
  Buildings,
  CaretDown,
  Globe,
  UploadSimple,
  PaperPlaneTilt,
  EnvelopeSimple,
  At,
  ChatText,
  MapPin,
  Briefcase,
} from "@phosphor-icons/react";

const sourceChildren = [
  { to: "/startupservice/recherche", label: "Recherche", icon: MagnifyingGlass, testid: "nav-recherche" },
  { to: "/startupservice/bulk", label: "Bulk Recherche", icon: Stack, testid: "nav-bulk" },
];

const topItems = [
  { to: "/leads", label: "Leads", icon: TableIcon, testid: "nav-leads" },
  { to: "/versand", label: "PDF-Versand", icon: PaperPlaneTilt, testid: "nav-versand" },
  { to: "/email-versand", label: "E-Mail Versand", icon: EnvelopeSimple, testid: "nav-email-versand" },
  { to: "/postfaecher", label: "Postfächer", icon: At, testid: "nav-postfaecher" },
  { to: "/email-prompts", label: "E-Mail Prompts", icon: ChatText, testid: "nav-email-prompts" },
  { to: "/vorlagen", label: "Vorlagen", icon: FilePdf, testid: "nav-vorlagen" },
  { to: "/ki-einstellungen", label: "KI-Namen", icon: Robot, testid: "nav-ki" },
];

const linkCls = ({ isActive }) =>
  `flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm transition-colors duration-150 ${
    isActive ? "bg-[#002FA7] text-white font-medium" : "text-zinc-600 hover:bg-zinc-100"
  }`;

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(true);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const childActive = location.pathname.startsWith("/startupservice");

  return (
    <div className="min-h-screen flex bg-[#FAFAFA]">
      <aside className="w-60 shrink-0 bg-white border-r border-zinc-200 flex flex-col fixed h-screen">
        <div className="h-16 flex items-center gap-2.5 px-5 border-b border-zinc-200">
          <div className="h-8 w-8 bg-[#002FA7] text-white grid place-items-center rounded-sm">
            <Buildings size={18} weight="bold" />
          </div>
          <span className="font-heading text-lg font-bold tracking-tight">GND Leads</span>
        </div>

        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          <div className="text-[10px] uppercase tracking-widest text-zinc-400 px-3 pt-1 pb-1">Quellen</div>

          {/* StartupService group */}
          <button
            onClick={() => setOpen((o) => !o)}
            data-testid="nav-group-startupservice"
            className={`w-full flex items-center justify-between px-3 py-2.5 rounded-sm text-sm transition-colors duration-150 ${
              childActive ? "text-[#002FA7] font-medium" : "text-zinc-600 hover:bg-zinc-100"
            }`}
          >
            <span className="flex items-center gap-3"><Globe size={18} />StartupService</span>
            <CaretDown size={14} className={`transition-transform duration-150 ${open ? "" : "-rotate-90"}`} />
          </button>
          {open && (
            <div className="ml-3 pl-2 border-l border-zinc-200 space-y-1">
              {sourceChildren.map((item) => (
                <NavLink key={item.to} to={item.to} data-testid={item.testid}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2 rounded-sm text-sm transition-colors duration-150 ${
                      isActive ? "bg-[#002FA7] text-white font-medium" : "text-zinc-600 hover:bg-zinc-100"
                    }`}>
                  <item.icon size={16} />
                  {item.label}
                </NavLink>
              ))}
            </div>
          )}

          {/* Google Maps source */}
          <NavLink to="/googlemaps" data-testid="nav-googlemaps" className={linkCls}>
            <MapPin size={18} />
            Google Maps
          </NavLink>

          {/* Job boards source */}
          <NavLink to="/jobboersen" data-testid="nav-jobboersen" className={linkCls}>
            <Briefcase size={18} />
            Jobbörsen
          </NavLink>

          {/* Manual upload source */}
          <NavLink to="/manuell/upload" data-testid="nav-manuell" className={linkCls}>
            <UploadSimple size={18} />
            Manueller Upload
          </NavLink>

          <div className="pt-2 mt-2 border-t border-zinc-100" />

          {topItems.map((item) => (
            <NavLink key={item.to} to={item.to} data-testid={item.testid} className={linkCls}>
              <item.icon size={18} />
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="border-t border-zinc-200 p-3">
          <div className="px-3 py-2 mb-1">
            <div className="text-xs uppercase tracking-widest text-zinc-400">Angemeldet</div>
            <div className="text-sm font-medium truncate" data-testid="current-user-email">{user?.email}</div>
          </div>
          <button onClick={handleLogout} data-testid="logout-button"
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm text-zinc-600 hover:bg-red-50 hover:text-[#E53935] transition-colors duration-150">
            <SignOut size={18} />
            Abmelden
          </button>
        </div>
      </aside>

      <main className="flex-1 ml-60 min-w-0">{children}</main>
    </div>
  );
}
